import { useEffect, useState } from "react";
import { PrimaryButton, RedButton } from "../../../components";
import {
  IBulkVoucherChanges,
  IBulkVoucherResult,
  IVoucher,
  bulkErrorMessage,
  useVouchers,
} from "../../../store/useVouchers";

type Field = keyof IBulkVoucherChanges;

interface FieldConfig {
  key: Field;
  label: string;
  kind: "input" | "textarea";
  rows?: number;
  placeholder?: string;
  hint?: string;
}

// Only text that product and code vouchers both have. Code, value, prize name
// and expiry date are changed one voucher at a time, or the batch is deleted
// and made again.
const FIELDS: FieldConfig[] = [
  {
    key: "description",
    label: "Description",
    kind: "textarea",
    rows: 3,
    placeholder: "Voucher description",
  },
  {
    key: "instructions",
    label: "Instructions",
    kind: "textarea",
    rows: 3,
    placeholder: "Instructions for the user",
  },
  {
    key: "link",
    label: "Link",
    kind: "input",
    placeholder: "e.g., https://www.partner.nl/actie",
    hint: "Shown as one tappable line at the bottom of the voucher.",
  },
  {
    key: "benefitText",
    label: "Benefit text",
    kind: "textarea",
    rows: 2,
    placeholder: "e.g., on all products",
    hint: "The small line under the value in the app's reward list.",
  },
];

/** What a field holds on one voucher, as the form shows it. */
const valueOf = (voucher: IVoucher | undefined, key: Field): string => {
  const raw = voucher?.[key];
  return typeof raw === "string" ? raw : "";
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** The vouchers to change. */
  ids: string[];
  /** One of the selected vouchers, to start a ticked field from its text. */
  sample?: IVoucher;
  onDone: (result: IBulkVoucherResult) => void;
}

/**
 * The same text on many vouchers at once. Only ticked fields are sent, so
 * everything else on each voucher is left as it is.
 */
export const BulkEditVouchersModal = ({
  open,
  onClose,
  ids,
  sample,
  onDone,
}: Props) => {
  const { bulkUpdateVouchers } = useVouchers();
  const [ticked, setTicked] = useState<Partial<Record<Field, boolean>>>({});
  const [values, setValues] = useState<Partial<Record<Field, string>>>({});
  const [includeIssued, setIncludeIssued] = useState(false);
  const [preview, setPreview] = useState<IBulkVoucherResult | null>(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setTicked({});
      setValues({});
      setIncludeIssued(false);
      setPreview(null);
      setError("");
      setIsSaving(false);
    }
  }, [open]);

  // The counts for the confirmation line. They do not depend on the fields,
  // only on the selection and on whether given-out vouchers are included.
  // Keyed on the ids' text so a new array with the same ids does not refetch.
  const idsKey = ids.join(",");
  useEffect(() => {
    if (!open || !ids.length) return;
    let cancelled = false;
    setPreview(null);
    setError("");
    bulkUpdateVouchers({ ids, changes: {}, includeIssued, dryRun: true })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(
            bulkErrorMessage(
              e,
              "Could not count the selected vouchers. Please try again."
            )
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey, includeIssued]);

  if (!open) return null;

  const tickedFields = FIELDS.filter((field) => ticked[field.key]);
  const canSave =
    !isSaving && tickedFields.length > 0 && (preview?.willChange ?? 0) > 0;

  const toggle = (key: Field, on: boolean) => {
    setTicked((prev) => ({ ...prev, [key]: on }));
    if (on && values[key] === undefined) {
      setValues((prev) => ({ ...prev, [key]: valueOf(sample, key) }));
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    const changes: IBulkVoucherChanges = {};
    for (const field of tickedFields) {
      changes[field.key] = values[field.key] ?? "";
    }
    setIsSaving(true);
    setError("");
    try {
      const result = await bulkUpdateVouchers({ ids, changes, includeIssued });
      onDone(result);
    } catch (e: any) {
      setError(
        bulkErrorMessage(
          e,
          "The vouchers could not be changed. Nothing was saved."
        )
      );
      setIsSaving(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400";

  return (
    <>
      <div className="justify-center items-center z-[10000000000] backdrop-blur-sm flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none">
        <div className="relative w-10/12 sm:w-1/2 my-0 sm:my-6 mx-auto max-w-3xl">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col max-h-[85vh] bg-white overflow-y-auto w-full outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <div>
                <h3 className="text-2xl font-semibold font-satoshi text-dark">
                  Edit {ids.length} {ids.length === 1 ? "voucher" : "vouchers"}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Tick the fields to change. Only ticked fields are saved, on
                  every selected voucher. A ticked empty field is cleared.
                </p>
              </div>
              <button
                className="text-gray-500"
                onClick={onClose}
                disabled={isSaving}
              >
                ✕
              </button>
            </div>
            <div className="relative p-6 flex-auto space-y-5">
              {FIELDS.map((field) => {
                const on = Boolean(ticked[field.key]);
                const value = values[field.key] ?? "";
                const setValue = (next: string) =>
                  setValues((prev) => ({ ...prev, [field.key]: next }));
                return (
                  <div key={field.key} data-field={field.key}>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => toggle(field.key, e.target.checked)}
                        disabled={isSaving}
                      />
                      Change {field.label.toLowerCase()}
                    </label>
                    {field.kind === "textarea" ? (
                      <textarea
                        className={inputClass}
                        rows={field.rows}
                        placeholder={field.placeholder}
                        value={value}
                        disabled={!on || isSaving}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    ) : (
                      <input
                        type={field.key === "link" ? "url" : "text"}
                        className={inputClass}
                        placeholder={field.placeholder}
                        value={value}
                        disabled={!on || isSaving}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    )}
                    {field.hint ? (
                      <p className="mt-1 text-xs text-gray-500">{field.hint}</p>
                    ) : null}
                  </div>
                );
              })}

              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-2">
                <label className="flex items-start gap-2 text-sm text-dark cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={includeIssued}
                    onChange={(e) => setIncludeIssued(e.target.checked)}
                    disabled={isSaving}
                  />
                  <span>
                    Also change vouchers already given to customers. Customers
                    see the new text in the app straight away.
                  </span>
                </label>
                <p className="text-sm text-dark" data-testid="bulk-preview">
                  {!preview && !error ? "Counting the selected vouchers..." : null}
                  {preview ? (
                    <>
                      <strong>{preview.willChange}</strong>{" "}
                      {preview.willChange === 1 ? "voucher" : "vouchers"} will
                      change.
                      {preview.issued > 0
                        ? includeIssued
                          ? ` This includes ${preview.issued} already given to customers.`
                          : ` ${preview.issued} already given to customers ${preview.issued === 1 ? "is" : "are"} left as ${preview.issued === 1 ? "it is" : "they are"}.`
                        : " None of them has been given to a customer yet."}
                      {preview.skippedDrop > 0
                        ? ` ${preview.skippedDrop} drop ${preview.skippedDrop === 1 ? "voucher is" : "vouchers are"} skipped.`
                        : ""}
                      {preview.notFound > 0
                        ? ` ${preview.notFound} no longer ${preview.notFound === 1 ? "exists" : "exist"}.`
                        : ""}
                    </>
                  ) : null}
                </p>
              </div>
              {error ? (
                <p className="text-sm text-red-600" data-testid="bulk-error">
                  {error}
                </p>
              ) : null}
            </div>
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              <RedButton
                label="Cancel"
                className="mr-4"
                onClick={() => !isSaving && onClose()}
              />
              <PrimaryButton
                label={
                  isSaving
                    ? "Saving..."
                    : preview
                      ? `Change ${preview.willChange} ${preview.willChange === 1 ? "voucher" : "vouchers"}`
                      : "Change vouchers"
                }
                onClick={handleSave}
                disabled={!canSave}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};
